/**
 * Main App Component for Lobby2.0
 * Modern React structure with hooks and styled-components
 */

import React, { useEffect, useRef, useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useAppStore, useUIState } from 'store/app/appStore';
import { useShallow } from 'zustand/react/shallow';
import { externalCommunication } from 'core/services/communication/ExternalCommunication';
// Features config used in conditional rendering
import { useInitializeLobby } from 'store/hooks/useInitializeLobby';
import { useTranslations } from 'shared/hooks/useTranslations';
import { useFontLoader, useFontsReady } from 'core/services/managers/FontLoader';
import { useSoundManager } from 'shared/hooks/useSound';
import { useOpenSection } from 'shared/hooks/useOpenSection';
import { viewportManager } from 'core/services/managers/ViewportManager';
import { useDeviceModeDetection } from 'shared/hooks/useDeviceModeDetection';
import LoadingIndicator from 'shared/components/LoadingIndicator';
import ViewControl from './ViewControl';
import { idleManager } from 'core/services/managers/IdleManager';
import SeasonalOverlays from 'shared/components/SeasonalOverlays';
import { trackerManager } from 'core/services/managers/TrackerManager';
import { useSamsungDarkModeDefense } from 'shared/hooks/useSamsungDarkModeDefense';

// Apply element styles like the old lobby
const applyElementStyles = (openType: string, mode: string, element: HTMLElement | null): void => {
  if (!element || !openType || !mode) {
    return;
  }

  if (openType === 'fullscreen') {
    if (element.classList.contains('egt_react_lobby_ingame')) {
      element.classList.replace('egt_react_lobby_ingame', 'egt_react_lobby_fullscreen');
    } else {
      element.classList.add('egt_react_lobby_fullscreen');
    }
  } else {
    if (element.classList.contains('egt_react_lobby_fullscreen')) {
      element.classList.replace('egt_react_lobby_fullscreen', 'egt_react_lobby_ingame');
    } else {
      element.classList.add('egt_react_lobby_ingame');
    }
  }
  if (mode === 'desktop') {
    if (element.classList.contains('egt_react_lobby_mobile')) {
      element.classList.replace('egt_react_lobby_mobile', 'egt_react_lobby_desktop');
    } else {
      element.classList.add('egt_react_lobby_desktop');
    }
  } else {
    if (element.classList.contains('egt_react_lobby_desktop')) {
      element.classList.replace('egt_react_lobby_desktop', 'egt_react_lobby_mobile');
    } else {
      element.classList.add('egt_react_lobby_mobile');
    }
  }
};

const AppContainer = styled.div<{ $openType: 'fullscreen' | 'ingame'; }>`
  width: 100%;
  /* Seasonal brandings keep the plain lobby background on purpose (no palette). */
  background: var(--appBackground, ${({ theme }) => theme.colors.appBackground});
  color: ${({ theme }) => theme.colors.textPrimary};
  font-family: var(--font-family-primary);
  overflow: hidden;
  position: fixed;
  top: 0;
  left: 0;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;

  /* Safe-area padding using variables populated by ViewportManager (fallback to 0) */
  /* padding-top: max(0px, var(--safe-top, 0px));
  padding-right: max(0px, var(--safe-right, 0px));
  padding-bottom: max(0px, var(--safe-bottom, 0px));
  padding-left: max(0px, var(--safe-left, 0px)); */
  
  /* Ingame mode specific styling */
  ${({ $openType }) =>
    $openType === 'ingame' &&
    `
       border-radius: 10px;
       overflow: hidden;
  `}
  
  /* Desktop and large screens use viewport units */
  @media (min-width: 769px) {
    height: 100vh;
    height: 100dvh;
  }
  
  /* Mobile Safari iframe-safe height calculation */
  @media (max-width: 768px) {
    /* Use CSS custom property set by ViewportManager */
    height: var(--app-height, 100vh);
    min-height: var(--app-height, 100vh);
  }
  
  /* iOS Safari specific fixes for iframe usage */
  @supports (-webkit-touch-callout: none) {
    @media (max-width: 768px) {
      height: var(--app-height, 100vh);
      min-height: var(--app-height, 100vh);
    }
  }
`;

const ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  padding: ${({ theme }) => theme.spacing['8']};
  text-align: center;
  background: ${({ theme }) => theme.colors.appBackground};
  color: ${({ theme }) => theme.colors.textPrimary};
`;

const ErrorTitle = styled.h1`
  font-size: ${({ theme }) => theme.typography.fontSizes['3xl']};
  margin-bottom: ${({ theme }) => theme.spacing['4']};
  color: ${({ theme }) => theme.colors.danger};
`;

const ErrorMessage = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSizes.lg};
  margin-bottom: ${({ theme }) => theme.spacing['6']};
  max-width: 500px;
  line-height: ${({ theme }) => theme.typography.lineHeights.relaxed};
`;

const RetryButton = styled.button`
  background: ${({ theme }) => theme.colors.primary};
  color: white;
  border: none;
  padding: ${({ theme }) => theme.spacing['3']} ${({ theme }) => theme.spacing['6']};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: ${({ theme }) => theme.typography.fontSizes.md};
  font-weight: ${({ theme }) => theme.typography.fontWeights.medium};
  cursor: pointer;
  transition: background-color ${({ theme }) => theme.transitions.duration.fast};

  &:hover {
    background: ${({ theme }) => theme.colors.primaryHover};
  }

  &:focus {
    outline: 2px solid ${({ theme }) => theme.colors.primary};
    outline-offset: 2px;
  }
`;

interface AppProps {
  elementId?: string;
}

// Component to display fetched settings
const App: React.FC<AppProps> = ({ elementId = 'lobby2-root' }) => {
  const { i18n } = useTranslation();
  const { settingsRequestDone, isVisible, isIdle, awaitingSettingsAfterHide } = useUIState();
  trackerManager.startStep('App.tsx render');

  // Optimize store selectors - combine related selectors to reduce subscriptions
  const { setVisible, triggerInitialization, mountLobby, openType } = useAppStore(
    useShallow((state) => ({
      setVisible: state.setVisible,
      setIdle: state.setIdle,
      triggerInitialization: state.triggerInitialization,
      mountLobby: state.settings.mountLobby,
      openType: state.settings.openType,
    })),
  );

  // Track if we've already handled auto-show to prevent re-execution
  const autoShowHandled = useRef(false);
  const initialCheckDone = useRef(false);
  
  // Use the new initialization hook
  const { 
    settingsData,
    isLoading: isSettingsLoading, 
    isError, 
    // canInitialize - available but not used in this component 
  } = useInitializeLobby();
  
  // Initialize translations system
  useTranslations();
  
  // Load fonts dynamically from assets server
  useFontLoader();
  // Hold the lobby behind the loader until its fonts are in, so text never swaps typeface
  const fontsReady = useFontsReady();
  
  // Initialize sound manager with proper re-initialization support
  useSoundManager();

  // Initialize device mode detection with resize listener
  useDeviceModeDetection();

  // Samsung Internet Browser dark mode defense
  useSamsungDarkModeDefense();

  // Handle openSection query parameter
  useOpenSection();

  // Initialize viewport manager
  useEffect(() => {
    // Clean up old localStorage from previous persist middleware (one-time migration)
    try {
      const oldStorageKey = 'lobby2-app-storage';
      if (localStorage.getItem(oldStorageKey)) {
        localStorage.removeItem(oldStorageKey);
        console.log('[App] Cleaned up legacy localStorage data');
      }
    } catch (error) {
      // Ignore localStorage errors (e.g., in private browsing)
    }

    // Initialize viewport manager for mobile Safari iframe fixes
    idleManager.registerProcess('view-port', () => viewportManager.initialize(), ()=> viewportManager.cleanup());

    // Apply element styles exactly like the old lobby
    const rootElement = document.getElementById(elementId);
    if (rootElement) {
      // Mode is already determined by useDeviceModeDetection (touch capability +
      // user agent, not viewport width) — reusing it here instead of recomputing
      // by width avoids overriding the correct desktop classification for
      // small-viewport iframes on non-touch devices.
      const mode = useAppStore.getState().settings.mode;

      applyElementStyles(openType, mode, rootElement);
    }
    
    // Cleanup on unmount
    return () => {
      idleManager.unregisterProcess('view-port');
    };
  }, [elementId, openType]);

  const urlAnalysis = useMemo(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const gameKeyValue = urlParams.get('gameKey');
    return {
      hasGameKeyParam: urlParams.has('gameKey'),
      gameKeyValue,
      hasValidGameKey: gameKeyValue && gameKeyValue.trim() !== '',
      isStandalone: window.self === window.top,
    };
  }, []);

  // Initialize external communication
  useEffect(() => {
    // Enhanced auto-show logic to handle empty gameKey scenarios
    if (!initialCheckDone.current) {
      initialCheckDone.current = true;

      const { hasGameKeyParam, hasValidGameKey, isStandalone } = urlAnalysis;

      // Auto-show lobby for empty gameKey scenarios (matches old lobby behavior)
      if (hasGameKeyParam && !hasValidGameKey) {
        // Empty gameKey detected, auto-showing lobby
        autoShowHandled.current = true;
        
        // OLD LOBBY APPROACH: Set UI visibility independent of Game Client element visibility
        setVisible(true);
        
        // Always trigger initialization for empty gameKey scenarios
        if (!mountLobby) {
          triggerInitialization();
        }
        
        // SEND DISPLAY EVENT: Mimic what Game Client should send but doesn't for empty gameKey
        // This matches old lobby's behavior where display events control UI visibility
        // Sending display event for empty gameKey
        externalCommunication.sendDisplay(true);
        
        
        // FALLBACK: If UI visibility doesn't work, force element visibility as backup
        // This ensures compatibility even if something goes wrong with UI-level control
        setTimeout(() => {
          const rootElement = document.getElementById('react-lobby-remote-root');
          if (rootElement && rootElement.style.opacity === '0') {
            // Fallback: Forcing element visibility
            rootElement.style.opacity = '1';
            rootElement.style.zIndex = '11';
          }
        }, 300);
      } else if (!hasGameKeyParam && isStandalone) {
        // Standalone lobby (no iframe, no gameKey param)
        // Standalone lobby detected, auto-showing
        autoShowHandled.current = true;
        setVisible(true);
        triggerInitialization();
      } else if (process.env['NODE_ENV'] === 'development' && isStandalone) {
        // Development mode standalone lobby - auto show
        autoShowHandled.current = true;
        setVisible(true);
        triggerInitialization();
      }
    }
    
    // Note: When valid gameKey is provided, we wait for display event from Game Client
  }, [setVisible, triggerInitialization, mountLobby, urlAnalysis]);

  // Update document language
  useEffect(() => {
    document.documentElement.lang = i18n.language;
  }, [i18n.language]);

  // Memoize render conditions to prevent unnecessary re-calculations
  const showViewControl = useMemo(
    () => isVisible && settingsRequestDone && !isSettingsLoading && !isError && !awaitingSettingsAfterHide && fontsReady,
    [isVisible, settingsRequestDone, isSettingsLoading, isError, awaitingSettingsAfterHide, fontsReady],
  );
const showLoadingIndicator = useMemo(
    () => isVisible && !isIdle && (isSettingsLoading || awaitingSettingsAfterHide || (!fontsReady && !isError)),
    [isVisible, isIdle, isSettingsLoading, awaitingSettingsAfterHide, fontsReady, isError],
  );
  useEffect(() => {
    trackerManager.completeStep('App.tsx render');
  }, []);

  return (
    <>
      <AppContainer id={elementId} $openType={openType}>
        <SeasonalOverlays lobbyReady={showViewControl} />
        
        {/* Show error if settings fetch failed */}
        {isError && (
          <ErrorContainer>
            <ErrorTitle>Failed to Load Settings</ErrorTitle>
            <ErrorMessage>
              Unable to fetch lobby configuration. Please try again.
            </ErrorMessage>
            <RetryButton onClick={() => window.location.reload()}>
              Retry
            </RetryButton>
          </ErrorContainer>
        )}
        
        {/* Show lobby when settings are loaded and visible */}
        {showViewControl ? (
          <ViewControl settingsData={settingsData} />
        ) : showLoadingIndicator && (
          // Not while idle: closing a campaign-only view sets awaitingSettingsAfterHide but the
          // client never echoes DISPLAY false, so the spinner would spin on in the hidden frame.
          <LoadingIndicator show />
        )}
        
      </AppContainer>
    </>
  );
};

export default App;
